import React from 'react';
import { SiteSettingModel, SUBJECT_TYPES, GRADE_TYPES, BOOK_TYPES, PUBLISHERS } from '../../../../models';
import { SelectionButton } from './SelectionButton';
import { Camera, ImageIcon, Book as BookIcon, Building2, ListOrdered, Plus, BookOpen, X } from 'lucide-react';

interface BookInfoProps {
    formData: any;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSelect: (name: string, value: string) => void;
    siteSettings: SiteSettingModel;
    coverInputRef: React.RefObject<HTMLInputElement>;
    handleCoverChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCoverDrop?: (file: File) => void;
    coverPreview: string | null;
    onRemoveCover?: () => void;
}

const BookInfo: React.FC<BookInfoProps> = ({
    formData,
    handleChange,
    handleSelect,
    siteSettings,
    coverInputRef,
    handleCoverChange,
    onCoverDrop,
    coverPreview,
    onRemoveCover
}) => {
    const [isDragging, setIsDragging] = React.useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/') && onCoverDrop) {
            onCoverDrop(file);
        }
    };

    const subjects = siteSettings.subjects && siteSettings.subjects.length > 0 ? siteSettings.subjects : SUBJECT_TYPES;
    const grades = siteSettings.bookGrades && siteSettings.bookGrades.length > 0 ? siteSettings.bookGrades : GRADE_TYPES;
    const bookTypes = siteSettings.bookTypes && siteSettings.bookTypes.length > 0 ? siteSettings.bookTypes : BOOK_TYPES;
    const publishers = siteSettings.publishers && siteSettings.publishers.length > 0 ? siteSettings.publishers : PUBLISHERS;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Section 0: Cover Image */}
            <section className="lg:col-span-3 bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center">
                <div className="w-full flex items-center space-x-2 text-indigo-600 mb-3">
                    <Camera size={16} />
                    <h3 className="text-sm font-bold">교재 표지</h3>
                </div>

                <div
                    onClick={() => coverInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative w-32 h-44 border-2 border-dashed rounded-xl overflow-hidden cursor-pointer transition-all group flex flex-col items-center justify-center text-center p-2 ${isDragging
                        ? 'bg-indigo-50 border-indigo-400 scale-105 shadow-lg shadow-indigo-100'
                        : 'bg-slate-50 border-slate-200 hover:border-indigo-300'
                        }`}
                >
                    {coverPreview ? (
                        <>
                            <img src={coverPreview} alt="Cover Preview" className="absolute inset-0 w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                                <Camera size={20} />
                            </div>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveCover?.();
                                }}
                                className="absolute top-2 right-2 z-20 p-1.5 bg-white hover:bg-rose-50 text-slate-500 hover:text-rose-600 rounded-full shadow-md border border-slate-200 transition-all hover:scale-105 active:scale-95 flex items-center justify-center"
                                title="표지 이미지 제거"
                            >
                                <X size={12} strokeWidth={3} />
                            </button>
                        </>
                    ) : (
                        <>
                            <div className={`p-2 rounded-full shadow-sm mb-2 transition-colors ${isDragging ? 'bg-white text-indigo-500' : 'bg-white text-slate-400'}`}>
                                <ImageIcon size={20} />
                            </div>
                            <p className="text-[10px] font-bold text-slate-500">이미지 선택 또는 드래그</p>
                        </>
                    )}
                </div>
                <input type="file" ref={coverInputRef} accept="image/*" className="hidden" onChange={handleCoverChange} />
            </section>

            {/* Right Column Stack */}
            <div className="lg:col-span-9 flex flex-col gap-4">
                {/* 1. 교재 제목 & 발행 연도 */}
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col gap-4">
                    <div className="grid grid-cols-4 gap-4">
                        <div className="col-span-3">
                            <label className="block text-xs font-bold text-indigo-600 mb-1.5">교재 제목</label>
                            <input
                                name="title" type="text" placeholder="교재 제목 입력" value={formData.title || ''} onChange={handleChange}
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                        </div>
                        <div className="col-span-1">
                            <label className="block text-[10px] font-bold text-slate-400 mb-1.5">발행 연도</label>
                            <input name="year" type="text" value={formData.year || ''} onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none" />
                        </div>
                    </div>
                </div>

                {/* 2. 분류 선택 */}
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col space-y-4">
                    <div className="flex items-center space-x-2 text-indigo-600">
                        <BookIcon size={16} />
                        <h3 className="text-sm font-bold">분류 선택</h3>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">과목</label>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {subjects.map(s => (
                                    <SelectionButton key={s} label={s} isSelected={formData.subject === s} onClick={() => handleSelect('subject', s)} />
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider flex items-center gap-1">
                                <Building2 size={12} /> 출판사
                            </label>
                            <div className="flex flex-wrap gap-1 max-h-[120px] overflow-y-auto pr-1 custom-scrollbar">
                                {publishers.map(p => (
                                    <SelectionButton key={p} label={p} isSelected={formData.publisher === p} onClick={() => handleSelect('publisher', p)} />
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">교재 대상</label>
                            <div className="flex flex-wrap gap-1">
                                {grades.map(g => (
                                    <SelectionButton key={g} label={g} isSelected={formData.grade === g} onClick={() => handleSelect('grade', g)} />
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">구분</label>
                            <div className="flex flex-wrap gap-1">
                                {bookTypes.map(t => (
                                    <SelectionButton key={t} label={t} isSelected={formData.type === t} onClick={() => handleSelect('type', t)} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default BookInfo;
